import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { nestedSeparator } from './integrations-types';
import type {
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
  List,
} from './integrations-types';

export type NewLookupValue = {
  name: string;
  label: string;
  listShortId: string;
};

export const buildLookupVariable = (
  newLookupValue: NewLookupValue,
): IntegrationEndpointSelectVariable => ({
  name: newLookupValue.name,
  label: newLookupValue.label,
  type: 'select',
  is_lookup: true,
  options_from: newLookupValue.listShortId,
  options: [],
});

const insertVariableBefore = ({
  variables,
  variable,
  beforeName,
}: {
  variables: IntegrationEndpointVariable[];
  variable: IntegrationEndpointVariable;
  beforeName: string;
}): IntegrationEndpointVariable[] => {
  const index = variables.findIndex((existing) => existing.name === beforeName);
  if (index === -1) return [...variables, variable];
  return [...variables.slice(0, index), variable, ...variables.slice(index)];
};

type ChangeFields = (
  fields: IntegrationEndpointVariable[],
  leafName: string,
) => IntegrationEndpointVariable[];

const updateFieldsAt = ({
  variables,
  flattenedName,
  changeFields,
}: {
  variables: IntegrationEndpointVariable[];
  flattenedName: string;
  changeFields: ChangeFields;
}): IntegrationEndpointVariable[] => {
  if (variables.some((variable) => variable.name === flattenedName)) {
    return changeFields(variables, flattenedName);
  }
  const separatorIndex = flattenedName.indexOf(nestedSeparator);
  if (separatorIndex === -1) {
    return changeFields(variables, flattenedName);
  }
  const parentName = flattenedName.slice(0, separatorIndex);
  const nestedName = flattenedName.slice(separatorIndex + nestedSeparator.length);
  const hasArrayParent = variables.some(
    (variable) => variable.name === parentName && variable.template,
  );
  if (!hasArrayParent) {
    return changeFields(variables, flattenedName);
  }
  return variables.map((variable) =>
    variable.name === parentName && variable.template
      ? {
          ...variable,
          template: updateFieldsAt({
            variables: variable.template,
            flattenedName: nestedName,
            changeFields,
          }),
        }
      : variable,
  );
};

export const flattenedLookupName = (beforeFlattenedName: string, lookupName: string): string => {
  const separatorIndex = beforeFlattenedName.lastIndexOf(nestedSeparator);
  if (separatorIndex === -1) return lookupName;
  return `${beforeFlattenedName.slice(0, separatorIndex + nestedSeparator.length)}${lookupName}`;
};

export const insertLookupBeforeField = ({
  variables,
  lookup,
  beforeFlattenedName,
}: {
  variables: IntegrationEndpointVariable[];
  lookup: IntegrationEndpointVariable;
  beforeFlattenedName: string;
}): { variables: IntegrationEndpointVariable[]; lookupName: string } => ({
  variables: updateFieldsAt({
    variables,
    flattenedName: beforeFlattenedName,
    changeFields: (fields, leafName) =>
      insertVariableBefore({
        variables: fields,
        variable: { ...lookup, name: flattenedLookupName(leafName, lookup.name) },
        beforeName: leafName,
      }),
  }),
  lookupName: flattenedLookupName(beforeFlattenedName, lookup.name),
});

export const removeVariableByName = (
  variables: IntegrationEndpointVariable[],
  flattenedName: string,
): IntegrationEndpointVariable[] =>
  updateFieldsAt({
    variables,
    flattenedName,
    changeFields: (fields, leafName) => fields.filter((variable) => variable.name !== leafName),
  });

// A deleted lookup may still be referenced as a parameter source by other
// selects' parameter_map — drop those references so they don't count as mapped.
export const removeNameFromParameterMaps = (
  variables: IntegrationEndpointVariable[],
  removedName: string,
): IntegrationEndpointVariable[] => {
  return variables.map((variable) => {
    let cleanedVariable = variable;
    const parameterMap = (variable as IntegrationEndpointSelectVariable).parameter_map;
    if (parameterMap && Object.values(parameterMap).includes(removedName)) {
      const remainingMap = Object.fromEntries(
        Object.entries(parameterMap).filter(([, variableName]) => variableName !== removedName),
      );
      cleanedVariable = {
        ...cleanedVariable,
        parameter_map: remainingMap,
      } as IntegrationEndpointVariable;
    }
    if (cleanedVariable.template) {
      cleanedVariable = {
        ...cleanedVariable,
        template: removeNameFromParameterMaps(cleanedVariable.template, removedName),
      };
    }
    return cleanedVariable;
  });
};

const Form = createForm<NewLookupValue>();

type AddLookupValueModalProps = {
  lists: List[];
  onSave: (newLookupValue: NewLookupValue) => Promise<void>;
  isNameTaken?: (name: string) => boolean;
};

export const AddLookupValueModal = ({ lists, onSave, isNameTaken }: AddLookupValueModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isSubmitting = formStore.useIsSubmitting();

  const validateNameFormat = (value: unknown) => {
    const text = typeof value === 'string' ? value : '';
    const name = text.trim();
    if (!name) return text ? t('lookupNameFormatError') : true;
    return /^[a-z][a-zA-Z0-9]*(_[a-zA-Z0-9]+)*$/.test(name) ? true : t('lookupNameFormatError');
  };

  const validateNameUnique = (value: unknown) => {
    const name = typeof value === 'string' ? value.trim() : '';
    if (!name || !isNameTaken) return true;
    return isNameTaken(name) ? t('lookupNameTakenError') : true;
  };

  const handleSubmit = async () => {
    const values = formStore.getState().values as NewLookupValue;
    await onSave({ ...values, name: (values.name ?? '').trim() });
    hideModal();
    return true;
  };

  const handleSave = () => formStore.actions.submit({ onSubmit: handleSubmit });

  return (
    <Modal isLoading={isSubmitting}>
      <Modal.SimpleHeader title={t('addLookupValue')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Stack space={4}>
            <Text text={t('lookupHelpText')} font="regular14" color="textSecondary" />
            <Form.TextField
              id="name"
              label={t('lookupNameLabel')}
              placeholder={t('lookupNamePlaceholder')}
              validators={['required', validateNameFormat, validateNameUnique]}
            />
            <Form.TextField
              id="label"
              label={t('lookupLabelLabel')}
              placeholder={t('lookupLabelPlaceholder')}
              defaultValue=""
              validators={['required']}
            />
            <Form.Select
              id="listShortId"
              label={t('lookupListLabel')}
              options={lists.map((list) => ({ value: list.id, label: list.name }))}
              placeholder={t('lookupListPlaceholder')}
              validators={['required']}
            />
          </Stack>
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSave}
          isDisabled={isSubmitting}
        />
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    addLookupValue: 'Add lookup value',
    lookupHelpText: 'Only used to populate dynamic lists — not sent in the request.',
    lookupNameLabel: 'Field',
    lookupNamePlaceholder: 'e.g. folder_id',
    lookupNameFormatError: 'Use a key like folder_id or folderId.',
    lookupNameTakenError: 'A field with this name already exists.',
    lookupLabelLabel: 'Label',
    lookupLabelPlaceholder: 'e.g. Folder',
    lookupListLabel: 'List',
    lookupListPlaceholder: 'Select list...',
  },
};
